/**
 * useSignedAvatarUrl.ts
 * src/shared/hooks/useSignedAvatarUrl.ts
 *
 * Resolves a bare avatars-bucket storage path (as stored in
 * profiles.avatar_url / children.profiles.avatar_url) to a short-lived
 * signed URL, now that the avatars bucket is private.
 *
 * - Signed URLs last 1 hour and are cached in memory only, for this
 *   session - never in localStorage, IndexedDB or the database. A
 *   background check re-signs a cached entry shortly before it expires
 *   while a component is still using it.
 * - No storage policy changes ride along with this: if the caller isn't
 *   allowed to sign a given path (e.g. viewing another Guardian's
 *   avatar), createSignedUrl/createSignedUrls simply fails for that
 *   path and this hook returns null - callers already fall back to an
 *   initials avatar whenever the url is null.
 * - useSignedAvatarUrls signs a whole list in one batched
 *   createSignedUrls call, for screens showing several avatars at once
 *   (Account Switcher, Tamariki roster, guardian pickers).
 */

import { useEffect, useState } from 'react'
import { supabase } from '../services/supabase'

const AVATAR_BUCKET = 'avatars'
const SIGN_EXPIRY_SECONDS = 60 * 60 // 1 hour
const REFRESH_MARGIN_MS = 5 * 60 * 1000 // re-sign with 5 min left on the clock
const REFRESH_CHECK_INTERVAL_MS = 5 * 60 * 1000

interface CacheEntry {
  url: string
  expiresAt: number // epoch ms
}

// Module-level, memory-only, session-lifetime cache. Never persisted.
const cache = new Map<string, CacheEntry>()
const inFlight = new Map<string, Promise<string | null>>()

function isFresh(entry: CacheEntry | undefined): entry is CacheEntry {
  return !!entry && entry.expiresAt - Date.now() > REFRESH_MARGIN_MS
}

/**
 * Drop a path's cached signed URL. Call right after re-uploading or
 * removing the file at that path, so the next read re-signs instead of
 * serving a URL that points at content which no longer matches.
 */
export function invalidateSignedAvatarUrl(path: string | null | undefined): void {
  if (!path) return
  cache.delete(path)
  inFlight.delete(path)
}

/**
 * Empty the entire in-memory signed-URL cache. Call this on sign-out
 * (the Supabase SIGNED_OUT auth event) so a signed URL scoped to one
 * user's session is never served after a different user signs in on the
 * same device.
 */
export function clearSignedAvatarCache(): void {
  cache.clear()
  inFlight.clear()
}

async function signOne(path: string): Promise<string | null> {
  const cached = cache.get(path)
  if (isFresh(cached)) return cached.url

  const existing = inFlight.get(path)
  if (existing) return existing

  const promise = supabase.storage
    .from(AVATAR_BUCKET)
    .createSignedUrl(path, SIGN_EXPIRY_SECONDS)
    .then(({ data, error }) => {
      inFlight.delete(path)
      if (error || !data?.signedUrl) {
        // Expected for paths the caller isn't allowed to sign (e.g.
        // another Guardian's avatar) - not necessarily a bug.
        cache.delete(path)
        return null
      }
      cache.set(path, { url: data.signedUrl, expiresAt: Date.now() + SIGN_EXPIRY_SECONDS * 1000 })
      return data.signedUrl
    })
    .catch((err) => {
      inFlight.delete(path)
      console.error('[useSignedAvatarUrl] sign failed:', path, err)
      return null
    })

  inFlight.set(path, promise)
  return promise
}

async function signMany(paths: string[]): Promise<Map<string, string | null>> {
  const result = new Map<string, string | null>()
  const toFetch: string[] = []

  for (const path of paths) {
    const cached = cache.get(path)
    if (isFresh(cached)) {
      result.set(path, cached.url)
    } else {
      toFetch.push(path)
    }
  }

  if (toFetch.length === 0) return result

  const { data, error } = await supabase.storage
    .from(AVATAR_BUCKET)
    .createSignedUrls(toFetch, SIGN_EXPIRY_SECONDS)

  if (error || !data) {
    console.error('[useSignedAvatarUrl] batch sign failed:', error?.message)
    for (const path of toFetch) result.set(path, null)
    return result
  }

  data.forEach((row, i) => {
    const path = toFetch[i]
    if (!row || row.error || !row.signedUrl) {
      result.set(path, null)
      return
    }
    cache.set(path, { url: row.signedUrl, expiresAt: Date.now() + SIGN_EXPIRY_SECONDS * 1000 })
    result.set(path, row.signedUrl)
  })

  return result
}

/**
 * Resolve a single bare storage path to a signed URL. Returns null for a
 * null/undefined path, or if signing fails - either way the caller
 * should render its initials fallback.
 */
export function useSignedAvatarUrl(path: string | null | undefined): { url: string | null; loading: boolean } {
  const [url, setUrl]         = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!path) {
      setUrl(null)
      setLoading(false)
      return
    }

    let cancelled = false
    const cached = cache.get(path)

    if (isFresh(cached)) {
      setUrl(cached.url)
      setLoading(false)
    } else {
      setLoading(true)
      signOne(path).then((signed) => {
        if (!cancelled) { setUrl(signed); setLoading(false) }
      })
    }

    // Proactively re-sign before the cached URL expires, as long as this
    // path is still in use somewhere on screen.
    const interval = setInterval(() => {
      if (!isFresh(cache.get(path))) {
        signOne(path).then((signed) => { if (!cancelled) setUrl(signed) })
      }
    }, REFRESH_CHECK_INTERVAL_MS)

    return () => { cancelled = true; clearInterval(interval) }
  }, [path])

  return { url, loading }
}

/**
 * Resolve a list of bare storage paths to signed URLs in one batched
 * createSignedUrls call - for screens showing several avatars at once.
 * Returns a map keyed by the original path; a path that failed to sign
 * (or wasn't provided) is simply absent/null, so callers fall back to
 * initials per-row as usual.
 */
export function useSignedAvatarUrls(paths: Array<string | null | undefined>): { urls: Record<string, string | null>; loading: boolean } {
  const validPaths = Array.from(new Set(paths.filter((p): p is string => !!p))).sort()
  const key = validPaths.join('|')

  const [urls, setUrls]       = useState<Record<string, string | null>>({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (validPaths.length === 0) {
      setUrls({})
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    signMany(validPaths).then((map) => {
      if (cancelled) return
      const next: Record<string, string | null> = {}
      map.forEach((v, k) => { next[k] = v })
      setUrls(next)
      setLoading(false)
    })

    const interval = setInterval(() => {
      const stale = validPaths.filter((p) => !isFresh(cache.get(p)))
      if (stale.length === 0) return
      signMany(stale).then((map) => {
        if (cancelled) return
        setUrls((prev) => {
          const next = { ...prev }
          map.forEach((v, k) => { next[k] = v })
          return next
        })
      })
    }, REFRESH_CHECK_INTERVAL_MS)

    return () => { cancelled = true; clearInterval(interval) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return { urls, loading }
}
